import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Clock, Images } from 'lucide-react'
import type { WeddingSettings } from '@shared/settings'
import type { PublicWedding } from '@shared/types'
import { ApiError, api, mensajeDeError } from '../../lib/api'
import { diasHasta } from '../../lib/format'
import { CordMark, CordSpine } from '../../ui/Cord'
import { Spinner } from '../../ui'

import {
  AccesoAPantalla,
  BOTON_CLARO,
  BOTON_CONTORNO,
  cx,
  tieneTexto,
  yaConfirmo,
} from './secciones/comunes'
import Portada from './secciones/Portada'
import Versiculo, { hayVersiculo } from './secciones/Versiculo'
import Historia, { hayHistoria } from './secciones/Historia'
import Padrinos, { hayPadrinos } from './secciones/Padrinos'
import EventoSeccion, { hayEvento } from './secciones/Evento'
import { hayPrograma } from './secciones/Programa'
import Vestimenta, { hayVestimenta } from './secciones/Vestimenta'
import Regalos, { hayRegalos } from './secciones/Regalos'
import LibroDeMensajes from './secciones/LibroDeMensajes'
import Preguntas, { hayPreguntas } from './secciones/Preguntas'
import TriviaSeccion, { hayTrivia } from './secciones/Trivia'
import Hashtag, { hayHashtag } from './secciones/Hashtag'

/* ===========================================================================
   La página que ve el invitado.

   El cordón de tres dobleces recorre el borde izquierdo como columna vertebral:
   se traza con el scroll y cada sección visible es un nudo. Todo el contenido
   cuelga a su derecha, en una sola columna alineada a la izquierda. Esa es
   toda la audacia del diseño; el resto se mantiene callado a propósito.
   =========================================================================== */

/** Ancho del riel del cordón. Angosto en el móvil, pero nunca ausente. */
const RIEL = 'w-[18px] sm:w-7 lg:w-9'
const RIEL_HUECO = 'pl-[18px] sm:pl-7 lg:pl-9'

/**
 * La línea que resume el programa sin desplegarlo: cuántos momentos hay y
 * entre qué horas. Sale de los datos, no de una promesa escrita a mano: si la
 * pareja agrega un momento a medianoche, la línea lo dice sola.
 */
function resumenDelPrograma(schedule: WeddingSettings['schedule']): string {
  const momentos = schedule.filter((momento) => tieneTexto(momento.title))
  const horas = momentos.map((momento) => momento.time.trim()).filter((hora) => hora !== '')
  const cuantos = `${momentos.length} ${momentos.length === 1 ? 'momento' : 'momentos'}`
  const primera = horas[0]
  const ultima = horas[horas.length - 1]
  return primera && ultima && primera !== ultima
    ? `${cuantos}, de ${primera} a ${ultima}`
    : cuantos
}

export default function BodaPublica({ slugForzado }: { slugForzado?: string } = {}) {
  // En "/" (fase de sitio personal) el slug no viene de la ruta: se fuerza a
  // la boda única de la instancia. En "/b/:slug" sigue viniendo de la URL.
  const { slug: slugDeRuta = '' } = useParams()
  const slug = slugForzado ?? slugDeRuta

  const [boda, setBoda] = useState<PublicWedding | null>(null)
  const [cargando, setCargando] = useState(true)
  const [fallo, setFallo] = useState<{ noEncontrada: boolean; mensaje: string } | null>(null)
  const [intento, setIntento] = useState(0)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    setFallo(null)

    api
      .get<{ wedding: PublicWedding }>(`/api/public/${encodeURIComponent(slug)}`)
      .then(({ wedding }) => {
        if (vigente) setBoda(wedding)
      })
      .catch((error: unknown) => {
        if (!vigente) return
        setBoda(null)
        setFallo({
          noEncontrada: error instanceof ApiError && error.esNoEncontrado,
          mensaje: mensajeDeError(error),
        })
      })
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [slug, intento])

  // El título de la pestaña es lo que se ve cuando comparten el enlace abierto.
  useEffect(() => {
    if (!boda) return
    const anterior = document.title
    document.title = `${boda.partner1Name} y ${boda.partner2Name}`
    return () => {
      document.title = anterior
    }
  }, [boda])

  const nudos = useNudos(boda !== null)
  const reintentar = useCallback(() => setIntento((n) => n + 1), [])

  if (cargando) return <Cargando />
  if (fallo?.noEncontrada) return <SinBoda />
  if (fallo) return <FalloDeCarga mensaje={fallo.mensaje} onReintentar={reintentar} />
  if (!boda) return <SinBoda />

  const ajustes = boda.settings
  const secciones = ajustes.sections
  const urlConfirmar = `/b/${boda.slug}/confirmar`

  // Pasado el día, pedir confirmación es ruido: lo que queda es mirar las fotos.
  const dias = diasHasta(boda.weddingDate)
  const pasada = dias !== null && dias < 0
  // El día de la boda la página cambia de oficio: acompaña en vez de invitar.
  const esHoy = dias === 0

  return (
    <div className="relative min-h-dvh bg-bruma">
      {/* El cordón: fijo, siempre presente, trazándose con el scroll. */}
      <div
        aria-hidden="true"
        className={cx('fixed inset-y-0 left-0 z-20 bg-tinta text-laton-claro', RIEL)}
      >
        <CordSpine knots={nudos} className="h-full w-full" />
      </div>

      {/* Acceso rápido de los novios: discreto para el invitado (se va con el
          scroll), a un toque para ustedes desde la portada. */}
      <Link
        to="/panel"
        aria-label="Entrar al panel de los novios"
        className="absolute right-4 top-3 z-30 inline-flex min-h-[40px] items-center rounded-full border border-bruma/25 px-4 text-xs font-medium tracking-wide text-bruma/80 transition-colors hover:bg-bruma/10 hover:text-bruma"
      >
        Panel
      </Link>

      <main className={RIEL_HUECO}>
        <Portada
          partner1Name={boda.partner1Name}
          partner2Name={boda.partner2Name}
          weddingDate={boda.weddingDate}
          tagline={ajustes.hero.tagline}
          dateNote={ajustes.hero.dateNote}
          imageUrl={ajustes.hero.imageUrl}
          videoUrl={ajustes.hero.videoUrl}
          fechaLimite={ajustes.rsvp.deadline}
          mostrarCuenta={secciones.cuentaRegresiva}
          pasada={pasada}
          urlConfirmar={urlConfirmar}
          esHoy={esHoy}
        />

        {secciones.versiculo && hayVersiculo(ajustes.verse) && (
          <Versiculo verse={ajustes.verse} />
        )}

        {secciones.historia && hayHistoria(ajustes.story) && <Historia story={ajustes.story} />}

        {secciones.padrinos && hayPadrinos(ajustes.party) && <Padrinos party={ajustes.party} />}

        {secciones.evento && hayEvento(ajustes.event) && (
          <EventoSeccion event={ajustes.event} weddingDate={boda.weddingDate} />
        )}

        {/* El programa no se despliega aquí: se anuncia y se abre. Dieciocho
            momentos eran 1445 px que había que recorrer para llegar a lo de
            abajo, y quien solo quería saber a qué hora entrar los pasaba
            todos. La cifra del resumen es real: se cuenta, no se promete. */}
        {secciones.programa && hayPrograma(ajustes.schedule) && (
          <AccesoAPantalla
            id="programa"
            eyebrow="El programa"
            titulo="Cómo va a ser el día"
            resumen={resumenDelPrograma(ajustes.schedule)}
            a={`/b/${boda.slug}/programa`}
            icono={<Clock size={22} aria-hidden="true" />}
          />
        )}

        {secciones.dressCode && hayVestimenta(ajustes.dressCode) && (
          <Vestimenta dressCode={ajustes.dressCode} />
        )}

        {secciones.regalos && hayRegalos(ajustes.gifts) && <Regalos gifts={ajustes.gifts} />}

        {/* La galería, igual: la rejilla completa vive en su pantalla. Aquí no
            se puede dar una cifra sin pedir las fotos a la API, y esta página
            no va a gastar una petición para decir un número. */}
        {secciones.galeria && (
          <AccesoAPantalla
            id="galeria"
            eyebrow="Galería"
            titulo="Las fotos de todos"
            resumen={
              ajustes.rsvp.allowGuestUploads
                ? 'Míralas y sube las tuyas'
                : 'Las fotos que suban los novios'
            }
            a={`/b/${boda.slug}/fotos`}
            icono={<Images size={22} aria-hidden="true" />}
          />
        )}

        {secciones.mensajes && (
          <LibroDeMensajes
            slug={boda.slug}
            permiteEscribir={ajustes.rsvp.allowMessages}
            modera={ajustes.rsvp.moderateMessages}
          />
        )}

        {secciones.faq && hayPreguntas(ajustes.faq) && <Preguntas faq={ajustes.faq} />}

        {secciones.trivia && hayTrivia(ajustes.trivia) && <TriviaSeccion trivia={ajustes.trivia} />}

        {secciones.hashtag && hayHashtag(ajustes.hashtag) && <Hashtag hashtag={ajustes.hashtag} />}

        <Pie branding={boda.branding} />
      </main>

      {esHoy && ajustes.rsvp.allowGuestUploads ? (
        /* El día de la boda el botón cambia de oficio: las fotos valen más que
           un RSVP tardío, incluso para quien ya confirmó hace meses.

           Apuntaba a `/fotos`, que NO era una ruta registrada: caía en la
           pantalla de «no encontrado». El único día en que este botón importa,
           llevaba a un callejón. Ahora `/fotos` existe —es la galería— pero el
           destino correcto para «comparte» es el que sube, no el que mira. */
        <BotonFlotante url={`/b/${boda.slug}/subir`}>Comparte tus fotos</BotonFlotante>
      ) : (
        !pasada && <BotonConfirmar slug={boda.slug} url={urlConfirmar} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Nudos del cordón: la posición 0..1 de cada sección visible
// ---------------------------------------------------------------------------

function iguales(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((valor, i) => Math.abs(valor - (b[i] ?? 0)) < 0.002)
}

/**
 * Mide dónde cae cada sección dentro del recorrido total del scroll. Se
 * recalcula cuando cambia el alto del documento —al cargar fotos, por ejemplo—
 * para que los nudos no se queden en el sitio equivocado.
 */
function useNudos(listo: boolean): number[] {
  const [nudos, setNudos] = useState<number[]>([])

  useEffect(() => {
    if (!listo) return

    let vivo = true
    let pendiente = false

    const medir = () => {
      pendiente = false
      if (!vivo) return

      const secciones = Array.from(document.querySelectorAll<HTMLElement>('[data-nudo]'))
      const recorrido = document.documentElement.scrollHeight - window.innerHeight
      // El nudo se ata cuando la sección entra en pantalla, no cuando toca el borde.
      const anticipo = window.innerHeight * 0.45

      const siguientes = secciones.map((nodo, i) => {
        if (recorrido <= 0) return secciones.length <= 1 ? 0 : i / (secciones.length - 1)
        const y = nodo.getBoundingClientRect().top + window.scrollY - anticipo
        return Math.min(1, Math.max(0, y / recorrido))
      })

      setNudos((previos) => (iguales(previos, siguientes) ? previos : siguientes))
    }

    const programar = () => {
      if (pendiente) return
      pendiente = true
      window.requestAnimationFrame(medir)
    }

    programar()
    window.addEventListener('resize', programar)

    // ResizeObserver no existe en navegadores viejos ni en jsdom.
    const observador =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(programar)
    observador?.observe(document.body)

    return () => {
      vivo = false
      window.removeEventListener('resize', programar)
      observador?.disconnect()
    }
  }, [listo])

  return nudos
}

// ---------------------------------------------------------------------------
// Botón fijo
// ---------------------------------------------------------------------------

/**
 * El botón que acompaña el scroll: aparece al pasar la portada y va abajo a
 * la derecha, donde llega el pulgar. Lo que pide depende del día.
 */
function BotonFlotante({ url, children }: { url: string; children: ReactNode }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const alDesplazar = () => setVisible(window.scrollY > window.innerHeight * 0.6)
    alDesplazar()
    window.addEventListener('scroll', alDesplazar, { passive: true })
    window.addEventListener('resize', alDesplazar)
    return () => {
      window.removeEventListener('scroll', alDesplazar)
      window.removeEventListener('resize', alDesplazar)
    }
  }, [])

  return (
    <div
      className={cx(
        'fixed bottom-4 right-4 z-30 transition-opacity duration-300',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <Link
        to={url}
        tabIndex={visible ? undefined : -1}
        aria-hidden={visible ? undefined : true}
        className="inline-flex min-h-[48px] items-center rounded-full bg-tinta px-6 text-sm font-medium text-bruma ring-1 ring-bruma/25 transition-colors hover:bg-tinta-suave"
      >
        {children}
      </Link>
    </div>
  )
}

/** Pide confirmar, y desaparece cuando el invitado ya confirmó en este navegador. */
function BotonConfirmar({ slug, url }: { slug: string; url: string }) {
  const [confirmado, setConfirmado] = useState(() => yaConfirmo(slug))

  useEffect(() => {
    const revisar = () => setConfirmado(yaConfirmo(slug))
    document.addEventListener('visibilitychange', revisar)
    window.addEventListener('focus', revisar)
    return () => {
      document.removeEventListener('visibilitychange', revisar)
      window.removeEventListener('focus', revisar)
    }
  }, [slug])

  if (confirmado) return null

  return <BotonFlotante url={url}>Confirmar asistencia</BotonFlotante>
}

// ---------------------------------------------------------------------------
// Estados de la página completa
// ---------------------------------------------------------------------------

function Cargando() {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-tinta px-6">
      <div className="flex flex-col items-start gap-4 text-palo">
        <CordMark className="h-4 w-10 text-laton-claro" />
        <p className="flex items-center gap-3 text-sm">
          <Spinner small />
          Abriendo la invitación…
        </p>
      </div>
    </main>
  )
}

function Envoltura({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center bg-bruma px-6 py-16 sm:px-12">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </main>
  )
}

function SinBoda() {
  return (
    <Envoltura>
      <CordMark className="h-4 w-10 text-laton" />
      <h1 className="mt-6 font-display text-titulo text-tinta">No encontramos esa boda</h1>
      <p className="mt-4 leading-relaxed text-palo-hondo">
        El enlace puede tener una letra de más, o los novios todavía no publicaron su página.
        Revísalo con quien te invitó: es una dirección del tipo{' '}
        <span className="font-mono text-tinta">/b/ana-y-carlos</span>.
      </p>
      <Link to="/" className={cx(BOTON_CONTORNO, 'mt-8')}>
        Ir a la invitación
      </Link>
    </Envoltura>
  )
}

function FalloDeCarga({ mensaje, onReintentar }: { mensaje: string; onReintentar: () => void }) {
  return (
    <Envoltura>
      <CordMark className="h-4 w-10 text-laton" />
      <h1 className="mt-6 font-display text-titulo text-tinta">No pudimos abrir la invitación</h1>
      <p className="mt-4 leading-relaxed text-palo-hondo">{mensaje}</p>
      <button type="button" onClick={onReintentar} className={cx(BOTON_CLARO, 'mt-8')}>
        Intentar de nuevo
      </button>
    </Envoltura>
  )
}

// ---------------------------------------------------------------------------
// Pie
// ---------------------------------------------------------------------------

/**
 * El pie siempre existe: es la puerta discreta al panel de los novios, que
 * desde que "/" sirve la boda ya no tiene otro camino visible. El crédito de
 * la plataforma, en cambio, solo aparece en el plan gratis.
 */
/**
 * El pie reserva sitio abajo para el botón flotante: los dos viven en la
 * esquina derecha, y al final del scroll la píldora tapaba justo el enlace
 * «Panel de los novios». Es holgura, no maquillaje — el botón mide 48 px más
 * su separación del borde.
 */
function Pie({ branding }: { branding: boolean }) {
  return (
    <footer className="bg-tinta px-5 pt-6 pb-24 sm:px-10 lg:px-16">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-3">
        {branding ? (
          // "/" ya es esta misma boda: el crédito enlaza a la portada de la
          // plataforma, no a un lugar que solo repetiría lo que ya se ve.
          <Link
            to="/plataforma"
            className="inline-flex items-center gap-3 text-xs tracking-wide text-palo transition-colors hover:text-bruma"
          >
            <CordMark className="h-3 w-7 text-laton-claro" />
            Hecho con Nuestra Boda
          </Link>
        ) : (
          <CordMark className="h-3 w-7 text-laton-claro" aria-hidden="true" />
        )}
        <Link
          to="/panel"
          className="text-xs tracking-wide text-palo/80 underline-offset-4 transition-colors hover:text-bruma hover:underline"
        >
          Panel de los novios
        </Link>
      </div>
    </footer>
  )
}
